import React, { useEffect, useRef, useState } from "react";
import mark from "../assets/ro-mark.png";
import { business } from "../data/business";
import { useSite } from "../hooks/useSite";

// Opening sequence for a full page load of the homepage, matching the
// reference's observed behaviour: solid brand-colour screen, logo rises from
// behind a mask (1000ms, ease-out-quart), logo fades (300ms), then after
// 200ms the curtain slides up (600ms, ease-out-quart) to reveal the page,
// and the element is removed 1000ms later. The reference replays on every
// full load and only on the homepage; in-app navigation never replays it.
const TIMING = { rise: 1000, fade: 300, curtainDelay: 200, curtain: 600, remove: 1000, assetWait: 1500, safety: 7000 };

export default function IntroLoader({ onDone }) {
  const { paused, reduced } = useSite();
  const [phase, setPhase] = useState("waiting"); // waiting | rise | fade | curtain | done
  const timers = useRef([]);
  const started = useRef(false);
  const instant = paused || reduced;

  const later = (fn, ms) => {
    const id = window.setTimeout(fn, ms);
    timers.current.push(id);
  };

  const start = () => {
    if (started.current) return;
    started.current = true;
    const t = instant ? { rise: 450, fade: 250, curtainDelay: 100, curtain: 300, remove: 100 } : TIMING;
    setPhase("rise");
    later(() => setPhase("fade"), t.rise);
    later(() => setPhase("curtain"), t.rise + t.fade + t.curtainDelay);
    later(() => {
      setPhase("done");
      if (onDone) onDone();
    }, t.rise + t.fade + t.curtainDelay + t.curtain + t.remove);
  };

  useEffect(() => {
    const pending = timers.current;
    document.body.classList.add("ro-locked");
    // The static pre-mount cover from index.html hands over to this component.
    const boot = document.getElementById("ro-boot");
    if (boot) boot.remove();
    // Begin when the mark has loaded, or after a short cap, so a slow or
    // failed asset can never hold the intro.
    const img = new Image();
    img.onload = start;
    img.onerror = start;
    img.src = mark;
    const cap = window.setTimeout(start, TIMING.assetWait);
    const safety = window.setTimeout(() => {
      setPhase("done");
      if (onDone) onDone();
    }, TIMING.safety);
    return () => {
      window.clearTimeout(cap);
      window.clearTimeout(safety);
      pending.forEach(window.clearTimeout);
      document.body.classList.remove("ro-locked");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Interaction returns as soon as the curtain starts leaving.
  useEffect(() => {
    if (phase === "curtain" || phase === "done") document.body.classList.remove("ro-locked");
  }, [phase]);

  if (phase === "done") return null;
  return (
    <div className={`ro-intro ro-intro--${phase} ${instant ? "ro-intro--instant" : ""}`} aria-hidden="true">
      <div className="ro-intro__curtain" />
      <div className="ro-intro__content">
        <div className="ro-intro__logo">
          <svg className="ro-intro__ring" viewBox="0 0 210 210">
            <defs>
              <path id="ro-intro-ring-path" d="M105,105m-84,0a84,84 0 1,1 168,0a84,84 0 1,1 -168,0" />
            </defs>
            <text>
              <textPath href="#ro-intro-ring-path" textLength="527">
                {business.ringText}
              </textPath>
            </text>
          </svg>
          <img src={mark} alt="" width="108" height="108" />
        </div>
      </div>
    </div>
  );
}
